/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { Link } from 'react-router';

import { snakeCase } from 'lodash';

import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/admin-dashboard';

const styles = {
  item: css`
    height: 100%;
    padding: 4px 30px 0 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom-width: 4px;
    border-bottom-style: solid;
    border-bottom-color: transparent;
    text-decoration: none !important;

    span {
      color: var(--cds-color-grey-600);
      font-size: 0.875rem;
    }

    &:hover {
      border-bottom-width: 4px;
      border-bottom-style: solid;
      border-bottom-color: var(--cds-color-interactive-primary);

      span {
        color: var(--cds-color-grey-975) !important;
      }
    }

    &:empty {
      display: none;
    }
  `,
  active: css`
    color: var(--cds-color-blue-600) !important;
    font-weight: bold;

    :hover {
      color: var(--cds-color-blue-600);
    }
  `,
};

type Props = {
  text: string;
  tag: React.ElementType;
  htmlAttributes: { href: string; 'data-e2e': string };
  onClick?: AnchorOnClick | LinkOnClick | ButtonOnClick;
  buttonType?: 'button' | 'submit' | 'reset';
  isActive?: boolean;
  thirdPartyOrganizationId?: string;
  isBetaFeature?: boolean;
  isNewFeature?: boolean;
};

type AnchorOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
type LinkOnClick = (event: React.MouseEvent<Link, MouseEvent>) => void;
type ButtonOnClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

const MenuItem: React.FC<Props> = ({
  text,
  isActive,
  isBetaFeature,
  htmlAttributes,
  tag = 'button',
  buttonType = 'button',
  thirdPartyOrganizationId,
  onClick,
}) => {
  const children = (
    <span css={isActive && styles.active}>
      {text}
      {isBetaFeature && (
        <span
          css={css`
            background-color: var(--cds-color-darken-700);
            font-weight: bold;
            color: var(--cds-color-neutral-primary-invert);
            border-radius: 10px;
            padding: 2px 8px;
            margin-left: 8px;
          `}
        >
          {_t('BETA')}
        </span>
      )}
    </span>
  );
  switch (tag) {
    case 'a': {
      const handleClick = onClick as AnchorOnClick;
      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <a css={styles.item} onClick={handleClick} {...htmlAttributes}>
          {children}
        </a>
      );
    }
    case 'link': {
      const { href, ...rest } = htmlAttributes;
      return (
        <TrackedReactLink
          trackingName={snakeCase(text)}
          data={{ thirdPartyOrganizationId }}
          css={styles.item}
          href={href}
          {...rest}
        >
          {children}
        </TrackedReactLink>
      );
    }
    case 'button': {
      const handleClick = onClick as ButtonOnClick;

      return (
        <button
          className="button-link text-decoration-none"
          onClick={handleClick}
          {...htmlAttributes}
          css={styles.item}
          // eslint-disable-next-line react/button-has-type
          type={buttonType}
        >
          {children}
        </button>
      );
    }
    default:
      return <span className="text-decoration-none inline-block">{children}</span>;
  }
};

export default MenuItem;
